import React from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Container,
  Input,
  Button,
  Alert,
  Spinner,
  UncontrolledPopover,
  PopoverBody,
} from 'reactstrap';
import { redirect, json } from '@remix-run/node';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigation, Form, useActionData, Link, useSearchParams } from '@remix-run/react';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { getSession, commitSession } from '../../sessions';
import PasswordInput from '../../components/PasswordInput';
import styles from '../../style/routes/login.css?url';

export const meta = () => {
  const pageTitle = `Register`;
  return [
    { title: `${pageTitle} | Best Money Moves` },
    {
      property: 'og:title',
      content: pageTitle,
    },
  ];
};

export const links = () => [{ rel: 'stylesheet', href: styles }];

export async function action({ request }) {
  try {
    const formData = await request.formData();

    const formValues = Object.fromEntries(formData);

    const { email, signUpCode, password, confirmPassword } = formValues;

    if (!email || !password || !signUpCode) {
      return json({
        error: 'Please provide an email, sign up code, and password.',
      });
    }

    if (password !== confirmPassword) {
      return json({
        error: 'Passwords must match.',
      });
    }

    const res = await fetch(`${process.env.V2_API}/selfservice/register`, {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(formValues),
    });

    const { jwt, errorMessage } = await res.json();

    if (!jwt) {
      return json({
        error: Array.isArray(errorMessage)
          ? errorMessage?.[0]
          : errorMessage || 'Invalid user, or incorrect sign up code.',
      });
    }

    const session = await getSession();

    session.set('token', jwt);

    return redirect('/', {
      headers: {
        'Set-Cookie': await commitSession(session),
      },
    });
  } catch (error) {
    return json(
      {
        error: 'Something went wrong. Please try again later.',
      },
      { status: 500 }
    );
  }
}

export default function Register() {
  const data = useActionData();

  const navigation = useNavigation();

  const [searchParams] = useSearchParams();

  const signUpCode = searchParams.get('signUpCode');

  return (
    <Container className="d-flex flex-column justify-content-center align-items-center">
      {data?.error && (
        <Alert color="danger" className="text-center w-100">
          {data?.error}
        </Alert>
      )}

      <Card className="w-100">
        <CardBody className="login-container my-3 d-flex flex-column align-items-center justify-content-around text-center">
          <CardTitle tag="h2">Create Your Account</CardTitle>
          <Form method="post" className="w-100 mt-5">
            <Input className="text-center mb-3" name="email" placeholder="Email" />

            {signUpCode ? (
              <Input
                hidden
                value={signUpCode}
                className="text-center mb-0"
                name="signUpCode"
                placeholder="Sign Up Code"
              />
            ) : (
              <Input className="text-center mb-0" name="signUpCode" placeholder="Sign Up Code" />
            )}

            {!signUpCode && (
              <p className="mb-3 text-secondary" style={{ fontSize: '.9rem' }}>
                You must have a user code to sign up.
                <FontAwesomeIcon icon={faQuestionCircle} id="popover" className="ms-1 text-primary cursor-pointer" />
                <UncontrolledPopover flip target="popover" placement="bottom-end">
                  <PopoverBody className="text-center">
                    A user code is required to register. If you do not have a user code, please reach out to your HR
                    department.
                  </PopoverBody>
                </UncontrolledPopover>
              </p>
            )}

            <PasswordInput className="text-center mb-3" type="password" name="password" placeholder="Create Password" />
            <PasswordInput
              className="text-center mb-5"
              type="password"
              name="confirmPassword"
              placeholder="Confirm Password"
            />
            <Button color="primary" className="mb-3 px-5" style={{ width: '9.5rem' }}>
              {navigation.formAction === '/register' ? <Spinner size="sm" /> : 'Create'}
            </Button>
          </Form>
          <div className="d-flex align-items-center gap-3 w-100 justify-content-center mb-2">
            <span>Already have an account?</span>
            <Link to="/login">
              <span>Login Here</span>
            </Link>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}
